import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Stack,
  CircularProgress
} from '@mui/material';
import { usePositionsGrouped } from '../../api/usePositions';
import { useCreateRule, useUpdateRule } from '../../api/useUnwinding';
import {
  UnwindingRule,
  CreateUnwindingRuleDto,
  UpdateUnwindingRuleDto
} from '../../types/unwinding.types';
import { PositionsGroupedByPair, PredictionPosition } from '../../types';

interface Props {
  open: boolean;
  onClose: () => void;
  editRule?: UnwindingRule | null;
}

export const CreateRuleDialog = ({ open, onClose, editRule }: Props) => {
  const { data: positionsData, isLoading: positionsLoading } =
    usePositionsGrouped({ status: 'open' });
  const createMutation = useCreateRule();
  const updateMutation = useUpdateRule();

  const [pairId, setPairId] = useState('');
  const [kalshiEnabled, setKalshiEnabled] = useState(false);
  const [polyEnabled, setPolyEnabled] = useState(false);
  const [kalshiTargetTna, setKalshiTargetTna] = useState('2');
  const [kalshiContracts, setKalshiContracts] = useState('');
  const [polyTargetTna, setPolyTargetTna] = useState('2');
  const [polyContracts, setPolyContracts] = useState('');
  const [maxVolumeUsd, setMaxVolumeUsd] = useState('5000');
  const [volumePeriod, setVolumePeriod] = useState<'hour' | 'day'>('day');

  // Derived state from selected pair
  const selectedGroup = useMemo(() => {
    if (!positionsData?.groups || !pairId) return null;
    return positionsData.groups.find(g => g._id === pairId) || null;
  }, [positionsData, pairId]);

  const kalshiPosition: PredictionPosition | undefined = useMemo(
    () => selectedGroup?.positions.find(p => p.platform === 'kalshi'),
    [selectedGroup]
  );

  const polyPosition: PredictionPosition | undefined = useMemo(
    () => selectedGroup?.positions.find(p => p.platform === 'polymarket'),
    [selectedGroup]
  );

  // Auto-populate when pair changes
  useEffect(() => {
    if (!editRule && selectedGroup) {
      if (kalshiPosition) {
        setKalshiEnabled(true);
        setKalshiContracts(String(kalshiPosition.contracts));
      } else {
        setKalshiEnabled(false);
      }
      if (polyPosition) {
        setPolyEnabled(true);
        setPolyContracts(String(polyPosition.contracts));
      } else {
        setPolyEnabled(false);
      }
    }
  }, [selectedGroup, kalshiPosition, polyPosition, editRule]);

  // Populate form when editing
  useEffect(() => {
    if (editRule) {
      setPairId(editRule.pairId);
      if (editRule.kalshiConfig) {
        setKalshiEnabled(true);
        setKalshiTargetTna(String(editRule.kalshiConfig.targetTnaPercent));
        setKalshiContracts(String(editRule.kalshiConfig.contractsToSell));
      } else {
        setKalshiEnabled(false);
      }
      if (editRule.polymarketConfig) {
        setPolyEnabled(true);
        setPolyTargetTna(String(editRule.polymarketConfig.targetTnaPercent));
        setPolyContracts(String(editRule.polymarketConfig.contractsToSell));
      } else {
        setPolyEnabled(false);
      }
      setMaxVolumeUsd(String(editRule.maxVolumeUsd));
      setVolumePeriod(editRule.volumePeriod);
    }
  }, [editRule]);

  // Reset form on close
  useEffect(() => {
    if (!open) {
      setPairId('');
      setKalshiEnabled(false);
      setPolyEnabled(false);
      setKalshiTargetTna('2');
      setKalshiContracts('');
      setPolyTargetTna('2');
      setPolyContracts('');
      setMaxVolumeUsd('5000');
      setVolumePeriod('day');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (editRule) {
      const dto: UpdateUnwindingRuleDto = {
        maxVolumeUsd: Number(maxVolumeUsd),
        volumePeriod
      };
      if (kalshiEnabled && kalshiPosition) {
        dto.kalshiConfig = {
          side: kalshiPosition.side,
          ticker: kalshiPosition.marketId,
          contractsToSell: Number(kalshiContracts),
          targetTnaPercent: Number(kalshiTargetTna)
        };
      }
      if (polyEnabled && polyPosition) {
        dto.polymarketConfig = {
          side: polyPosition.side,
          tokenId: polyPosition.marketId,
          conditionId: polyPosition.marketId,
          contractsToSell: Number(polyContracts),
          targetTnaPercent: Number(polyTargetTna)
        };
      }
      await updateMutation.mutateAsync({ id: editRule._id, dto });
    } else {
      const dto: CreateUnwindingRuleDto = {
        pairId,
        maxVolumeUsd: Number(maxVolumeUsd),
        volumePeriod
      };
      if (kalshiEnabled && kalshiPosition) {
        dto.kalshiConfig = {
          side: kalshiPosition.side,
          ticker: kalshiPosition.marketId,
          contractsToSell: Number(kalshiContracts),
          targetTnaPercent: Number(kalshiTargetTna)
        };
      }
      if (polyEnabled && polyPosition) {
        dto.polymarketConfig = {
          side: polyPosition.side,
          tokenId: polyPosition.marketId,
          conditionId: polyPosition.marketId,
          contractsToSell: Number(polyContracts),
          targetTnaPercent: Number(polyTargetTna)
        };
      }
      await createMutation.mutateAsync(dto);
    }
    onClose();
  };

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;
  const canSubmit =
    pairId &&
    (kalshiEnabled || polyEnabled) &&
    Number(maxVolumeUsd) > 0 &&
    (!kalshiEnabled || Number(kalshiContracts) > 0) &&
    (!polyEnabled || Number(polyContracts) > 0);

  const openPositions: PositionsGroupedByPair[] = useMemo(
    () => positionsData?.groups ?? [],
    [positionsData]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem' }}>
        {editRule ? 'Edit Unwinding Rule' : 'Create Unwinding Rule'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* Pair selector */}
          <FormControl fullWidth size="small" disabled={!!editRule}>
            <InputLabel>Position Pair</InputLabel>
            <Select
              value={pairId}
              onChange={e => setPairId(e.target.value)}
              label="Position Pair"
            >
              {positionsLoading && (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Loading...
                </MenuItem>
              )}
              {openPositions.map(g => (
                <MenuItem key={g._id} value={g._id || ''}>
                  {g.pairName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Kalshi Config */}
          <Box
            sx={{
              p: 1.5,
              border: '1px solid',
              borderColor: kalshiEnabled ? 'primary.main' : 'grey.800',
              borderRadius: 1
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={kalshiEnabled}
                  onChange={e => setKalshiEnabled(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  Kalshi Config
                </Typography>
              }
            />
            {kalshiEnabled && (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {kalshiPosition && (
                  <Typography sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
                    Side: {kalshiPosition.side.toUpperCase()} | Held:{' '}
                    {kalshiPosition.contracts} contracts
                  </Typography>
                )}
                <TextField
                  label="Target TNA %"
                  type="number"
                  size="small"
                  value={kalshiTargetTna}
                  onChange={e => setKalshiTargetTna(e.target.value)}
                  inputProps={{ step: 0.5, min: 0 }}
                  fullWidth
                />
                <TextField
                  label="Contracts to Sell"
                  type="number"
                  size="small"
                  value={kalshiContracts}
                  onChange={e => setKalshiContracts(e.target.value)}
                  inputProps={{ step: 1, min: 1 }}
                  fullWidth
                />
              </Stack>
            )}
          </Box>

          {/* Polymarket Config */}
          <Box
            sx={{
              p: 1.5,
              border: '1px solid',
              borderColor: polyEnabled ? 'primary.main' : 'grey.800',
              borderRadius: 1
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={polyEnabled}
                  onChange={e => setPolyEnabled(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  Polymarket Config
                </Typography>
              }
            />
            {polyEnabled && (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {polyPosition && (
                  <Typography sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
                    Side: {polyPosition.side.toUpperCase()} | Held:{' '}
                    {polyPosition.contracts} contracts
                  </Typography>
                )}
                <TextField
                  label="Target TNA %"
                  type="number"
                  size="small"
                  value={polyTargetTna}
                  onChange={e => setPolyTargetTna(e.target.value)}
                  inputProps={{ step: 0.5, min: 0 }}
                  fullWidth
                />
                <TextField
                  label="Contracts to Sell"
                  type="number"
                  size="small"
                  value={polyContracts}
                  onChange={e => setPolyContracts(e.target.value)}
                  inputProps={{ step: 1, min: 1 }}
                  fullWidth
                />
              </Stack>
            )}
          </Box>

          {/* Volume limits */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Max Volume USD"
              type="number"
              size="small"
              value={maxVolumeUsd}
              onChange={e => setMaxVolumeUsd(e.target.value)}
              inputProps={{ step: 100, min: 0 }}
              fullWidth
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={volumePeriod}
                onChange={e =>
                  setVolumePeriod(e.target.value as 'hour' | 'day')
                }
                label="Period"
              >
                <MenuItem value="hour">Per Hour</MenuItem>
                <MenuItem value="day">Per Day</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          size="small"
        >
          {isSubmitting ? (
            <CircularProgress size={16} />
          ) : editRule ? (
            'Save'
          ) : (
            'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
